import { useState } from 'react';
import {
  Button,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { ArrowUndo20Regular, Save20Regular } from '@fluentui/react-icons';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { TagSwitch, type TagSwitchOption } from '@/components/common/TagSwitch';
import { useTelemetry } from '@/hooks/useTelemetry';
import { ParametersFormEditor } from './ParametersFormEditor';
import type { ParametersEditorState } from './useParametersEditor';

type EditorMode = 'ui' | 'json';

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Keep horizontal overflow visible so tab focus isn't clipped by tabList's negative margin.
    overflowY: 'hidden',
    height: '100%',
  },
  viewRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    flexShrink: 0,
  },
  tabList: {
    // Fluent <Tab size="small"> ships with intrinsic horizontal padding of
    // `spacingHorizontalSNudge`. Pulling the TabList left by the same token
    // lands the first tab's content flush with the section <h2> above.
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalSNudge})`,
    marginBottom: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  editorSurface: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  jsonError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
  footerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

export interface ParametersViewProps {
  state: ParametersEditorState;
}

const parameterViews: readonly TagSwitchOption<'production' | 'draft'>[] = [
  { value: 'production', label: 'Production' },
  { value: 'draft', label: 'Draft' },
];

/**
 * Parameters editor surface. Renders either a form view or a JSON view of the
 * same underlying state — the parent supplies the JSON string and change
 * callback, so Save / Discard / draft-mode work identically regardless of
 * which mode is active.
 */
export const ParametersView = ({ state }: ParametersViewProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const {
    userCanEdit,
    permissionsLoading,
    isLoading,
    isError,
    refetch,
    isDraftMode,
    setIsDraftMode,
    rawJson,
    jsonError,
    isValidJson,
    handleJsonChange,
  } = state;

  const [mode, setMode] = useState<EditorMode>('ui');

  const readOnly = permissionsLoading || !userCanEdit;

  return (
    <div className={styles.root}>
      <div className={styles.viewRow}>
        <TagSwitch
          aria-label="Parameters view"
          data-testid="parameters-view-switch"
          options={parameterViews}
          value={isDraftMode ? 'draft' : 'production'}
          onChange={(value) => {
            setIsDraftMode(value === 'draft');
            trackEvent('parameters.view.changed', { view: value });
          }}
        />
      </div>
      {isLoading ? (
        <TableSkeleton rows={4} columns={2} label="Loading parameters..." />
      ) : isError ? (
        <div className={styles.empty}>
          <Text>
            Failed to load parameters.{' '}
            <Button appearance="transparent" onClick={() => refetch()}>
              Retry
            </Button>
          </Text>
        </div>
      ) : (
        <>
          <TabList
            className={styles.tabList}
            aria-label="Parameters editor mode"
            selectedValue={mode}
            onTabSelect={(_, data) => setMode(data.value as EditorMode)}
            size="small"
          >
            <Tab value="ui">Form</Tab>
            <Tab value="json">JSON</Tab>
          </TabList>
          <div className={styles.editorSurface}>
            {mode === 'ui' ? (
              <ParametersFormEditor
                value={rawJson}
                onChange={handleJsonChange}
                onValidityChange={state.setFormError}
                readOnly={readOnly}
              />
            ) : (
              <>
                <JsonEditor
                  value={rawJson}
                  onChange={handleJsonChange}
                  fill
                  hasError={!isValidJson}
                  readOnly={readOnly}
                />
                {jsonError && <Text className={styles.jsonError}>{jsonError}</Text>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

ParametersView.displayName = 'ParametersView';

export interface ParametersFooterActionsProps {
  state: ParametersEditorState;
}

/**
 * Save/Discard actions for the Parameters editor. Always renders so the
 * surrounding container's height stays stable; the buttons disable themselves
 * when their action isn't applicable.
 */
export const ParametersFooterActions = ({ state }: ParametersFooterActionsProps) => {
  const styles = useStyles();
  const {
    userCanEdit,
    permissionsLoading,
    hasChanges,
    isValidJson,
    formError,
    isSaving,
    handleSave,
    handleDiscard,
  } = state;
  const cantEditYet = !userCanEdit || permissionsLoading;
  const saveBlocked = cantEditYet || !hasChanges || !isValidJson || formError !== null || isSaving;

  return (
    <div className={styles.footerActions}>
      {formError && <Text className={styles.jsonError}>{formError}</Text>}
      <Button
        appearance="secondary"
        icon={<ArrowUndo20Regular />}
        onClick={handleDiscard}
        disabled={cantEditYet || !hasChanges || isSaving}
      >
        Discard
      </Button>
      <Button
        appearance="primary"
        icon={<Save20Regular />}
        onClick={handleSave}
        disabled={saveBlocked}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
};

ParametersFooterActions.displayName = 'ParametersFooterActions';
